import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Truck, Download, Loader2, ExternalLink } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api, API } from "@/lib/api";
import { GODADDY_DONATE_URL } from "@/lib/links";

const HERO = "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const ProductCard = ({ p, index }) => {
  const [size, setSize] = useState(p.sizes?.[0] || "");
  const [color, setColor] = useState(p.colors?.[0] || "");
  const soldOut = p.stock !== null && p.stock !== undefined && p.stock <= 0;

  return (
    <motion.div
      initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={index}
      data-testid="product-card"
      className="bg-white border border-slate-200 hover:border-[#D90429] transition-colors duration-200 flex flex-col overflow-hidden group"
    >
      <div className="relative h-64 bg-slate-100 overflow-hidden">
        {p.image_file_id ? (
          <img src={`${API}/public/files/${p.image_file_id}`} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-slate-300" /></div>
        )}
        <span className="absolute top-3 left-3 bg-[#0A1128] text-white text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 inline-flex items-center gap-1">
          {p.requires_shipping ? <><Truck className="w-3 h-3" /> Ships</> : <><Download className="w-3 h-3" /> Digital</>}
        </span>
        {soldOut && <span className="absolute top-3 right-3 bg-slate-700 text-white text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1">Sold Out</span>}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-xl uppercase leading-tight text-[#0A1128]">{p.name}</h3>
        {p.description && <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{p.description}</p>}
        <p className="font-display text-2xl text-[#D90429] mt-3">${p.price.toFixed(2)}</p>

        {(p.sizes?.length > 0 || p.colors?.length > 0) && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {p.sizes?.length > 0 && (
              <select data-testid={`product-size-${p.id}`} value={size} onChange={(e) => setSize(e.target.value)} className="border border-slate-300 px-2 py-2 text-sm focus:outline-none focus:border-[#D90429]">
                {p.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {p.colors?.length > 0 && (
              <select data-testid={`product-color-${p.id}`} value={color} onChange={(e) => setColor(e.target.value)} className="border border-slate-300 px-2 py-2 text-sm focus:outline-none focus:border-[#D90429]">
                {p.colors.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        )}

        <div className="mt-4">
          {soldOut ? (
            <span className="w-full inline-flex items-center justify-center gap-1.5 bg-slate-200 text-slate-400 px-3 py-3 uppercase tracking-[0.1em] text-xs font-bold cursor-not-allowed">Sold Out</span>
          ) : (
            <a
              href={p.godaddy_url || GODADDY_DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`buy-godaddy-${p.id}`}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-3 py-3 uppercase tracking-[0.1em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99]"
            >
              <ShoppingBag className="w-4 h-4" /> Buy Now <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Navbar />

      <PageHero
        image={HERO}
        imageAlt="Patriot merchandise"
        kicker="Gear Up, Patriot"
        title={<>The <span className="text-[#D90429]">Store</span></>}
        subtitle="Wear your values. Every purchase supports the mission. Apparel, flags, and gear for those who stand ready."
      />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
          ) : products.length === 0 ? (
            <p className="text-slate-400 text-center py-20">New products dropping soon — check back shortly.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
